import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Spin } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';
import { targetsAPI } from '../services/api';

const TargetStats = ({ targetId, filters = {} }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!targetId) return;

    setLoading(true);
    try {
      // Use filters from props or default to 5m
      const queryParams = { timeRange: filters.timeRange || '5m' };

      // Add custom date range if provided
      if (filters.startDate && filters.endDate) {
        queryParams.startDate = filters.startDate;
        queryParams.endDate = filters.endDate;
      }

      const response = await targetsAPI.getTargetStats(targetId, queryParams);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching target stats:', error);
    } finally {
      setLoading(false);
    }
  }, [targetId, filters]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getUptimeColor = (percentage) => {
    if (percentage >= 99) return '#52c41a';
    if (percentage >= 95) return '#faad14';
    return '#ff4d4f';
  };

  const getUptimeIcon = (percentage) => {
    if (percentage >= 99) return <ArrowUpOutlined />;
    if (percentage >= 95) return <MinusOutlined />;
    return <ArrowDownOutlined />;
  };

  if (loading || !stats) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', padding: '20px' }} />;
  }

  const uptimePercentage = stats.total_checks > 0
    ? ((stats.up_count / stats.total_checks) * 100).toFixed(2)
    : 0;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} md={8}>
        <Card>
          <Statistic
            title="Total Checks"
            value={stats.total_checks}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={8}>
        <Card>
          <Statistic
            title="Uptime Percentage"
            value={uptimePercentage}
            suffix="%"
            valueStyle={{ color: getUptimeColor(uptimePercentage) }}
            prefix={getUptimeIcon(uptimePercentage)}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={8}>
        <Card>
          <Statistic
            title="Average Latency"
            value={stats.avg_latency}
            suffix="ms"
            precision={2}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Up Count"
            value={stats.up_count}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Down Count"
            value={stats.down_count}
            valueStyle={{ color: '#ff4d4f' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Timeout Count"
            value={stats.timeout_count}
            valueStyle={{ color: '#faad14' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Min Latency"
            value={stats.min_latency}
            suffix="ms"
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Max Latency"
            value={stats.max_latency}
            suffix="ms"
          />
        </Card>
      </Col>
    </Row>
  );
};

export default TargetStats;